import {entry, objectFromEntries} from '@benchling/js-utils';
import React, {useEffect, useState} from 'react';
import _ from 'underscore';

import ArchiveRecordLabel from 'archive/components/ArchiveRecordLabel';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {ConnectionStatus} from 'instruments/connections/ConnectionConstants';
import {getInstrumentServiceDetails, listInstrumentTypes} from 'instruments/connections/ConnectionDAO';
import ConnectionStatusDisplay from 'instruments/connections/ConnectionStatusDisplay';
import {InstrumentGateway} from 'instruments/instrument-gateways/InstrumentGatewayConstants';
import {getGateway} from 'instruments/instrument-gateways/InstrumentGatewayDAO';
import BaseListingRendererWithPlugins from 'nav/expanded/BaseListingRendererWithPlugins';
import {
  ColumnAccessorKey,
  ColumnConfiguration,
  ListingRendererType,
} from 'nav/expanded/ListingRendererConstants';
import {ContextMenuExtras} from 'nav/expanded/RenderExpandedListing';
import {getPartyDisplayString} from 'parties/GetPartyDisplayString';
import {SortKey} from 'search/SearchConstants';
import {getColumnHeaderFromSortKey} from 'search/SortUtil';
import BioentityChip from 'ui-library/chips/BioentityChip';
import {ChipSize} from 'ui-library/chips/Chip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {TABLE_ROW_BODY_HEIGHT} from 'ui-library/scrollable/ScrollableTableConstants';
import {DatetimeTableCell} from 'ui-library/scrollable/table-cells/DatetimeTableCell';
import {BasicEvent} from 'util/Types';

import {ConnectionListingGraphQL} from '../../../../graphql-fragments/instruments/__generated/ConnectionListingGraphQL';
import * as ConnectionContextMenu from './ConnectionContextMenu';

export interface ConnectionListingExtras {}

// Cache the instrument types lookup so each row's AdapterTypeDisplay shares a single fetch
// rather than calling listInstrumentTypes() once per connection in the listing.
let instrumentTypeDisplayNamesByIdPromise: Promise<{[adapterId: string]: string}> | null = null;

function getInstrumentTypeDisplayNamesById(): Promise<{[adapterId: string]: string}> {
  if (instrumentTypeDisplayNamesByIdPromise == null) {
    instrumentTypeDisplayNamesByIdPromise = listInstrumentTypes()
      .then(({instrumentAdapters}) =>
        objectFromEntries(instrumentAdapters.map((adapter) => entry(adapter.id, adapter.displayName)))
      )
      .catch((error) => {
        // Clear the cache so a later render can retry rather than being stuck on a rejected promise.
        instrumentTypeDisplayNamesByIdPromise = null;
        throw error;
      });
  }
  return instrumentTypeDisplayNamesByIdPromise;
}

// Exposed for tests to reset the module-level instrument types cache between cases.
export function resetInstrumentTypeDisplayNamesCacheForTesting(): void {
  instrumentTypeDisplayNamesByIdPromise = null;
}

function AdapterTypeDisplay(props: {adapterId: string}): JSX.Element {
  const [displayName, setDisplayName] = useState<string | null>(null);

  useEffect((): (() => void) => {
    let isMounted = true;
    getInstrumentTypeDisplayNamesById()
      .then((displayNamesById) => {
        if (isMounted) {
          setDisplayName(displayNamesById[props.adapterId] ?? props.adapterId);
        }
      })
      .catch(() => {
        // On failure, fall back to the adapter id rather than leaving the column blank.
        if (isMounted) {
          setDisplayName(props.adapterId);
        }
      });
    return () => {
      isMounted = false;
    };
  }, [props.adapterId]);

  // Render nothing until the display name resolves, rather than flashing the adapter system name.
  return <span>{displayName}</span>;
}

function GatewayDisplay(props: {gatewayId: string}): JSX.Element {
  const [gateway, setGateway] = useState(null as InstrumentGateway | null);

  useEffect((): void => {
    getGateway(props.gatewayId).then((gateway) => {
      setGateway(gateway);
    });
  }, [props.gatewayId]);

  return <span>{gateway ? gateway.name : 'Loading...'}</span>;
}

function StatusDisplay(props: {connectionId: string}): JSX.Element {
  const [status, setStatus] = useState(null as ConnectionStatus | null);

  useEffect((): void => {
    getInstrumentServiceDetails(props.connectionId).then((instrumentServiceDetails) => {
      setStatus(instrumentServiceDetails.status.value);
    });
  }, [props.connectionId]);

  return status != null ? <ConnectionStatusDisplay status={status} /> : <span>Loading...</span>;
}

export default class ConnectionListingRenderer extends BaseListingRendererWithPlugins<ListingRendererType.CONNECTION> {
  listingRendererType = ListingRendererType.CONNECTION;

  renderIcon(): JSX.Element {
    return <BenchlingIcon icon={ModelTypeConstants.CONNECTION} className="scrollableTable-cell-icon" />;
  }

  getNonPluginColumnConfigurations(
    items: Array<ConnectionListingGraphQL>
  ): Array<ColumnConfiguration<ConnectionListingGraphQL>> {
    const columns: Array<ColumnConfiguration<ConnectionListingGraphQL>> = _.compact([
      {
        column: {
          accessorKey: ColumnAccessorKey.ID,
          header: getColumnHeaderFromSortKey(SortKey.SORT_IDENTIFIER_KEY),
          flexGrow: 1,
          isSortable: true,
          sortKey: SortKey.SORT_IDENTIFIER_KEY,
          width: 200,
        },
        render: (item) => (
          <span key="name" className="sidepanel-primaryLabel">
            {item.humanId}
          </span>
        ),
      },
      {
        column: {
          header: getColumnHeaderFromSortKey(SortKey.SORT_NAME_KEY),
          accessorKey: ColumnAccessorKey.NAME,
          isCollapsible: false,
          isSortable: true,
          sortKey: SortKey.SORT_NAME_KEY,
          flexGrow: 1,
        },
        render: (item) => (
          <span className="u-ellipsis-text">
            {item.name}
            <ArchiveRecordLabel item={item} />
          </span>
        ),
      },
      {
        column: {
          header: 'Associated Equipment',
          accessorKey: ColumnAccessorKey.CONNECTION_EQUIPMENT,
          isCollapsible: false,
          isSortable: false,
          flexGrow: 1,
        },
        render: (item) => (
          <>
            {Array.from(item.associatedEquipment.entries()).map(([index, equipment]) => (
              <BioentityChip
                className="u-mini-spacing-top"
                id={equipment.id}
                isLink={true}
                isInaccessible={!equipment.id}
                key={index}
                size={ChipSize.MINI}
              />
            ))}
          </>
        ),
        isHidden: (_) => !BenchlingConfig.ENABLE_EQUIPMENT,
      },

      {
        column: {
          accessorKey: ColumnAccessorKey.SCHEMA,
          header: getColumnHeaderFromSortKey(SortKey.SORT_CONNECTION_ADAPTER),
          flexGrow: 1,
          isSortable: true,
          sortKey: SortKey.SORT_CONNECTION_ADAPTER,
        },
        render: (item) => <AdapterTypeDisplay adapterId={item.adapterId} />,
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.CONNECTION_STATUS,
          header: 'Status',
          flexGrow: 1,
          isSortable: false,
        },
        render: (item) => <StatusDisplay connectionId={item.id} />,
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.CONNECTION_GATEWAY,
          header: 'Benchling gateway',
          flexGrow: 1,
          isSortable: false,
        },
        render: (item) => <GatewayDisplay gatewayId={item.gatewayId} />,
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.OWNER,
          header: 'Owner',
          flexGrow: 1,
          width: 200,
          isCollapsible: false,
        },
        render: (item) => <div>{item.ownerJson.name}</div>,
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.CREATOR,
          header: getColumnHeaderFromSortKey(SortKey.SORT_CREATOR_NAME),
          flexGrow: 1,
          isSortable: true,
          sortKey: SortKey.SORT_CREATOR_NAME,
        },
        render: (item) => getPartyDisplayString(item.creator),
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.CREATED_AT,
          header: getColumnHeaderFromSortKey(SortKey.SORT_CREATED_AT_KEY),
          flexGrow: 1,
          isSortable: true,
          sortKey: SortKey.SORT_CREATED_AT_KEY,
        },
        render: (item) => <DatetimeTableCell datetime={item.createdAt} showTimeInCell={false} />,
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.MODIFIED_AT,
          header: getColumnHeaderFromSortKey(SortKey.SORT_MODIFIED_AT_KEY),
          flexGrow: 1,
          isSortable: true,
          sortKey: SortKey.SORT_MODIFIED_AT_KEY,
        },
        render: (item) => <DatetimeTableCell datetime={item.modifiedAt} showTimeInCell={false} />,
      },
    ]);

    return columns;
  }

  getNonPluginRowContentHeight(): number {
    return TABLE_ROW_BODY_HEIGHT;
  }

  openContextMenu(item: ConnectionListingGraphQL, e: BasicEvent, extras: ContextMenuExtras): void {
    ConnectionContextMenu.open(e.nativeEvent, item, extras);
  }
}
